// src/components/BidModal.js — sexy polished 2025 look ✨
import React, { useState, useEffect } from "react";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "react-hot-toast";
import { X, DollarSign, Send, Loader2 } from "lucide-react";

const API_WRK = process.env.REACT_APP_API_WRK;
const API_BASE = API_WRK?.replace("/api/worker", "");

const BidModal = ({ job, workerId, onClose }) => {
  const [amount, setAmount] = useState(job.budget || "");
  const [message, setMessage] = useState("");
  const [loading, setLoading] = useState(false);
  const [estimatedEarnings, setEstimatedEarnings] = useState(0);
  const [completedJobs, setCompletedJobs] = useState(0);

  // 🔁 Fetch how many jobs worker has completed
  useEffect(() => {
    const fetchCompleted = async () => {
      try {
        const res = await axios.get(`${API_BASE}/api/worker/completed-jobs/${workerId}`);
        setCompletedJobs(res.data.count || 0);
      } catch {
        toast.error("Could not fetch completed jobs count");
      }
    };
    if (workerId) fetchCompleted();
  }, [workerId]);

  // 💰 Fee logic
  const calculateEarnings = (amount) => {
    const bid = parseFloat(amount);
    if (isNaN(bid)) return 0;

    if (completedJobs < 5) return bid;
    if (bid < 100) return bid - 4.49;
    if (bid < 250) return bid * 0.92;
    if (bid < 500) return bid * 0.93;
    if (bid < 1000) return bid * 0.94;
    return bid * 0.95;
  };

  useEffect(() => {
    setEstimatedEarnings(calculateEarnings(amount).toFixed(2));
  }, [amount, completedJobs]);

  // 📨 Submit bid
  const submitBid = async () => {
    if (!amount || parseFloat(amount) <= 0)
      return toast.error("Please enter a valid bid amount.");

    try {
      setLoading(true);
      await axios.post(`${API_BASE}/api/worker/job/submit-bid`, {
        jobId: job._id,
        workerId,
        price: parseFloat(amount),
        message,
      });

      toast.success("✅ Bid submitted successfully!");
      onClose();
    } catch (err) {
      toast.error(err?.response?.data?.message || "Failed to submit bid");
    } finally {
      setLoading(false);
    }
  };

  return (
    <AnimatePresence>
      <motion.div
        className="fixed inset-0 bg-black/40 backdrop-blur-sm flex items-center justify-center z-50"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
      >
        <motion.div
          initial={{ scale: 0.9, opacity: 0, y: 40 }}
          animate={{ scale: 1, opacity: 1, y: 0 }}
          exit={{ scale: 0.9, opacity: 0, y: 40 }}
          transition={{ duration: 0.25 }}
          className="bg-white dark:bg-gray-900 relative p-6 rounded-2xl shadow-2xl w-[90%] max-w-md font-[Poppins] border border-gray-200 dark:border-gray-700"
        >
          {/* Close button */}
          <button
            onClick={onClose}
            className="absolute top-3 right-3 text-gray-500 hover:text-gray-800 dark:text-gray-400 dark:hover:text-white"
          >
            <X size={22} />
          </button>

          {/* Header */}
          <h2 className="text-2xl font-bold mb-1 text-center text-blue-600 dark:text-blue-400">
            Submit Your Bid
          </h2>
          <p className="text-sm text-center text-gray-600 dark:text-gray-400 mb-6">
            Set your offer price and include an optional message for the customer
          </p>

          <div className="bg-gray-50 dark:bg-gray-800 p-4 rounded-xl mb-5 text-sm space-y-1">
            <p>
              <strong className="text-gray-800 dark:text-gray-100">Job:</strong>{" "}
              {job.jobTitle}
            </p>
            <p className="text-gray-600 dark:text-gray-400">
              Budget: <strong>${job.budget}</strong>
            </p>
            <p className="text-green-700 dark:text-green-400 font-medium">
              💰 You’ll earn approx. <strong>${estimatedEarnings}</strong>{" "}
              {completedJobs < 5
                ? "(No platform fees applied 🆓)"
                : "after platform fees"}
            </p>
          </div>

          {/* Input fields */}
          <label className="block mb-2 font-medium text-gray-700 dark:text-gray-300">
            Your Bid Amount ($)
          </label>
          <div className="relative mb-4">
            <DollarSign className="absolute left-3 top-3 text-gray-400" size={18} />
            <input
              type="number"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              className="w-full pl-9 border border-gray-300 dark:border-gray-600 dark:bg-gray-800 dark:text-white p-2 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none"
              placeholder="Enter your bid amount"
              min={1}
            />
          </div>

          <label className="block mb-2 font-medium text-gray-700 dark:text-gray-300">
            Message to Customer (optional)
          </label>
          <textarea
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            className="w-full border border-gray-300 dark:border-gray-600 dark:bg-gray-800 dark:text-white p-2 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none mb-4"
            rows={3}
            placeholder="Add any additional details..."
          />

          {/* Buttons */}
          <div className="flex gap-3 mt-4">
            <button
              onClick={onClose}
              className="flex-1 bg-gray-200 hover:bg-gray-300 text-gray-800 dark:bg-gray-700 dark:hover:bg-gray-600 dark:text-white py-2 rounded-lg font-semibold transition"
            >
              Cancel
            </button>

            <button
              onClick={submitBid}
              disabled={loading}
              className={`flex-1 flex justify-center items-center gap-2 py-2 rounded-lg font-semibold text-white transition ${
                loading
                  ? "bg-blue-400 cursor-not-allowed"
                  : "bg-blue-600 hover:bg-blue-700 shadow-md shadow-blue-200 dark:shadow-blue-900"
              }`}
            >
              {loading ? (
                <>
                  <Loader2 className="animate-spin" size={18} /> Submitting...
                </>
              ) : (
                <>
                  <Send size={18} /> Submit Bid
                </>
              )}
            </button>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

export default BidModal;
